import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Users, UserSquare, Plane, Gauge, Settings, Shield, Package, Cog, Warehouse, Receipt, Workflow, Tag, Mail, Layers, CalendarClock } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "../utils/cn";
import { AppShell } from "../layouts/AppShell";
import { listAmbassadorOrgs, listFarms, listOrganizations } from "../../api/admin";
import { hasPermissionInCurrentOrg } from "../../state/orgContext";
type DashboardPageProps = {
  userName?: string;
  onLogout?: () => void;
  onOpenClients?: () => void;
  onOpenAmbassadors?: () => void;
  onOpenPilots?: () => void;
  onOpenKpis?: () => void;
  onOpenSettings?: () => void;
  onOpenOperations?: () => void;
  onOpenInventories?: () => void;
  onOpenAdminUsers?: () => void;
  onOpenAdminProducts?: () => void;
  onOpenAdminPromotions?: () => void;
  onOpenAdminProcesses?: () => void;
  onOpenAdminBilling?: () => void;
  onOpenAdminEmailTemplates?: () => void;
  onOpenEmailSend?: () => void;
  onOpenBookings?: () => void;
  onOpenStudio?: () => void;
  isSuperAdmin?: boolean;
};

type DashboardCardModel = {
  title: string;
  subtitle: string;
  meta: string;
  tone: string;
  icon: LucideIcon;
  onClick?: () => void;
};

const DashboardCard = memo(function DashboardCard({ card }: { card: DashboardCardModel }) {
  const Icon = card.icon;
  return (
    <button
      type="button"
      onClick={card.onClick}
      className="group rounded-md border border-border bg-surface p-4 text-left shadow-sm transition duration-150 ease-out hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-text">{card.title}</div>
          <div className="mt-1 text-xs text-muted">{card.subtitle}</div>
        </div>
        <div className={cn("flex h-9 w-9 items-center justify-center rounded-full border", card.tone)}>
          <Icon size={16} strokeWidth={1.5} />
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <span className={cn("text-xs font-medium", card.tone)}>{card.meta}</span>
        <span className="text-xs text-muted group-hover:text-text">Ouvrir</span>
      </div>
    </button>
  );
});

export function DashboardPage({
  userName = "Utilisateur",
  onLogout,
  onOpenClients,
  onOpenAmbassadors,
  onOpenPilots,
  onOpenKpis,
  onOpenSettings,
  onOpenOperations,
  onOpenInventories,
  onOpenAdminUsers,
  onOpenAdminProducts,
  onOpenAdminPromotions,
  onOpenAdminProcesses,
  onOpenAdminBilling,
  onOpenAdminEmailTemplates,
  onOpenEmailSend,
  onOpenBookings,
  onOpenStudio,
  isSuperAdmin = false,
}: DashboardPageProps) {
  const [clientsActive, setClientsActive] = useState<number>(0);
  const [ambassadorsActive, setAmbassadorsActive] = useState<number>(0);
  const [pilotsActive, setPilotsActive] = useState<number>(0);
  const canReadOpsEngagements = hasPermissionInCurrentOrg("ops.engagements.read");
  const canBookings = hasPermissionInCurrentOrg("commercial.bookings.read");

  useEffect(() => {
    let mounted = true;
    Promise.all([
      listFarms(1, 1, { status: "active" }),
      listAmbassadorOrgs({ status: "active", page: 1, page_size: 1 }),
      listOrganizations({ type: "drone_pilot_org", status: "active", page: 1, page_size: 1 }),
    ])
      .then(([farmsData, ambassadorsData, pilotsData]) => {
        if (!mounted) return;
        setClientsActive(farmsData?.meta?.total ?? 0);
        setAmbassadorsActive(ambassadorsData?.meta?.total ?? 0);
        setPilotsActive(pilotsData?.meta?.total ?? 0);
      })
      .catch((error) => {
        const msg = error?.message ?? String(error);
        const isSessionError = msg.includes("Session expirée") || msg.includes("Invalid token") || msg.includes("401");
        if (!isSessionError) {
          console.error("Failed to load dashboard counters:", error);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleOpenClients = useCallback(() => onOpenClients?.(), [onOpenClients]);
  const handleOpenAmbassadors = useCallback(() => onOpenAmbassadors?.(), [onOpenAmbassadors]);
  const handleOpenPilots = useCallback(() => onOpenPilots?.(), [onOpenPilots]);
  const handleOpenKpis = useCallback(() => onOpenKpis?.(), [onOpenKpis]);
  const handleOpenSettings = useCallback(() => onOpenSettings?.(), [onOpenSettings]);
  const handleOpenOperations = useCallback(() => onOpenOperations?.(), [onOpenOperations]);
  const handleOpenInventories = useCallback(() => onOpenInventories?.(), [onOpenInventories]);
  const handleOpenAdminUsers = useCallback(() => onOpenAdminUsers?.(), [onOpenAdminUsers]);
  const handleOpenAdminProducts = useCallback(() => onOpenAdminProducts?.(), [onOpenAdminProducts]);
  const handleOpenAdminPromotions = useCallback(() => onOpenAdminPromotions?.(), [onOpenAdminPromotions]);
  const handleOpenAdminProcesses = useCallback(() => onOpenAdminProcesses?.(), [onOpenAdminProcesses]);
  const handleOpenAdminBilling = useCallback(() => onOpenAdminBilling?.(), [onOpenAdminBilling]);
  const handleOpenAdminEmailTemplates = useCallback(() => onOpenAdminEmailTemplates?.(), [onOpenAdminEmailTemplates]);
  const handleOpenEmailSend = useCallback(() => onOpenEmailSend?.(), [onOpenEmailSend]);
  const handleOpenBookings = useCallback(() => onOpenBookings?.(), [onOpenBookings]);
  const handleOpenStudio = useCallback(() => onOpenStudio?.(), [onOpenStudio]);

  // Build modules list with section headers
  const commercialModules = [
    { label: "Commercial", sectionHeader: true },
    { label: "Tableau de bord", onClick: handleOpenKpis },
    { label: "Envoi de courriel", onClick: handleOpenEmailSend },
    ...(canBookings ? [{ label: "Rendez-vous", onClick: handleOpenBookings }] : []),
    { label: "Clients", onClick: handleOpenClients },
    { label: "Ambassadeurs", onClick: handleOpenAmbassadors },
    { label: "Pilotes", onClick: handleOpenPilots },
    { label: "Soumissions" },
    { label: "KPIs", onClick: handleOpenKpis },
    { label: "Paramètres", onClick: handleOpenSettings },
  ];

  const operationsModules = [
    { label: "Opération", sectionHeader: true, separator: true },
    ...(canReadOpsEngagements ? [{ label: "Opérations", onClick: handleOpenOperations }] : []),
    { label: "Studio", onClick: handleOpenStudio },
    { label: "Inventaires", onClick: handleOpenInventories },
  ];

  const adminModules = isSuperAdmin
    ? [
        { label: "Administration", sectionHeader: true, separator: true },
        { label: "Utilisateurs", onClick: handleOpenAdminUsers },
        { label: "Services et Produits", onClick: handleOpenAdminProducts },
        { label: "Promotions", onClick: handleOpenAdminPromotions },
        { label: "Processus", onClick: handleOpenAdminProcesses },
        { label: "Templates de courriel", onClick: handleOpenAdminEmailTemplates },
        { label: "Facturation", onClick: handleOpenAdminBilling },
      ]
    : [];

  const modules = [...commercialModules, ...operationsModules, ...adminModules];
  const commercialCards = useMemo(
    () => [
      { title: "Envoi de courriel", subtitle: "Template et liste de destinataires", meta: "Envoi individuel", tone: "text-danger", icon: Mail, onClick: handleOpenEmailSend },
      ...(canBookings
        ? [
            {
              title: "Rendez-vous",
              subtitle: "Disponibilités et liens clients",
              meta: "Réservation en ligne",
              tone: "text-info" as const,
              icon: CalendarClock,
              onClick: handleOpenBookings,
            },
          ]
        : []),
      { title: "Clients", subtitle: "Gestion des comptes", meta: `${clientsActive} actifs`, tone: "text-success", icon: Users, onClick: handleOpenClients },
      { title: "Ambassadeurs", subtitle: "Réseau & suivi", meta: `${ambassadorsActive} partenaires`, tone: "text-info", icon: UserSquare, onClick: handleOpenAmbassadors },
      { title: "Pilotes", subtitle: "Opérations terrain", meta: `${pilotsActive} actifs`, tone: "text-warning", icon: Plane, onClick: handleOpenPilots },
      { title: "KPIs", subtitle: "Performance & indicateurs", meta: "Derniers 30 jours", tone: "text-info", icon: Gauge, onClick: handleOpenKpis },
      { title: "Settings", subtitle: "Configuration", meta: "Mises à jour", tone: "text-muted", icon: Settings, onClick: handleOpenSettings },
    ],
    [
      ambassadorsActive,
      clientsActive,
      handleOpenAmbassadors,
      handleOpenClients,
      canBookings,
      handleOpenBookings,
      handleOpenEmailSend,
      handleOpenKpis,
      handleOpenPilots,
      handleOpenSettings,
      pilotsActive,
    ]
  );

  const operationsCards = useMemo(
    () => [
      ...(canReadOpsEngagements
        ? [{ title: "Opérations", subtitle: "Dossiers V2", meta: "En cours", tone: "text-warning" as const, icon: Cog, onClick: handleOpenOperations }]
        : []),
      { title: "Studio", subtitle: "Rule Sets & Process Templates", meta: "Orchestration", tone: "text-info" as const, icon: Layers, onClick: handleOpenStudio },
      { title: "Inventaires", subtitle: "Suivi des stocks", meta: "Disponible", tone: "text-warning" as const, icon: Warehouse, onClick: handleOpenInventories },
    ],
    [canReadOpsEngagements, handleOpenInventories, handleOpenOperations, handleOpenStudio]
  );

  const adminCards = useMemo(
    () => [
      { title: "Utilisateurs", subtitle: "Gestion des utilisateurs", meta: "Accès & rôles", tone: "text-danger", icon: Shield, onClick: handleOpenAdminUsers },
      { title: "Services et Produits", subtitle: "Catalogue", meta: "Configuration", tone: "text-danger", icon: Package, onClick: handleOpenAdminProducts },
      { title: "Promotions", subtitle: "Gestion des promotions", meta: "Rabais & offres", tone: "text-danger", icon: Tag, onClick: handleOpenAdminPromotions },
      { title: "Processus", subtitle: "Gestion des processus", meta: "Workflows", tone: "text-danger", icon: Workflow, onClick: handleOpenAdminProcesses },
      { title: "Templates de courriel", subtitle: "Modèles d'emails", meta: "Processus & alertes", tone: "text-danger", icon: Mail, onClick: handleOpenAdminEmailTemplates },
      { title: "Facturation", subtitle: "Gestion financière", meta: "Comptabilité", tone: "text-danger", icon: Receipt, onClick: handleOpenAdminBilling },
    ],
    [handleOpenAdminBilling, handleOpenAdminEmailTemplates, handleOpenAdminProcesses, handleOpenAdminProducts, handleOpenAdminPromotions, handleOpenAdminUsers]
  );

  return (
    <AppShell
      topbar={<div className="text-sm text-muted">Tableau de bord</div>}
      userName={userName}
      modules={modules}
      userMenuItems={[
        { label: "Profil", onClick: () => (window.location.hash = "#/profile") },
        { label: "Paramètres", onClick: () => (window.location.hash = "#/settings") },
        { label: "Déconnexion", onClick: onLogout },
      ]}
    >
      <div className="space-y-6">
        <div>
          <h1 className="text-section text-text">Tableau de bord</h1>
          <p className="text-sm text-muted">Accès rapide aux modules principaux</p>
        </div>

        {/* Section Commercial */}
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-text uppercase tracking-wider">Commercial</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {commercialCards.map((card) => (
              <DashboardCard key={card.title} card={card} />
            ))}
          </div>
        </div>

        {/* Séparateur */}
        <div className="border-t border-border"></div>

        {/* Section Opération */}
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-text uppercase tracking-wider">Opération</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {operationsCards.map((card) => (
              <DashboardCard key={card.title} card={card} />
            ))}
          </div>
        </div>

        {/* Séparateur (seulement si admin) */}
        {isSuperAdmin && (
          <>
            <div className="border-t border-border"></div>

            {/* Section Administration */}
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-text uppercase tracking-wider">Administration</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {adminCards.map((card) => (
                  <DashboardCard key={card.title} card={card} />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
